import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Loader2, ArrowLeft, MessageCircle, Send, Bug, Sparkles, HelpCircle,
  CreditCard, MoreHorizontal, Paperclip, X, Lock, Unlock,
} from "lucide-react";

const THREAD_TYPES = [
  { id: "bug", label: "Bug", icon: Bug, tone: "bg-red-100 text-red-800 border-red-200" },
  { id: "feature", label: "Feature request", icon: Sparkles, tone: "bg-purple-100 text-purple-800 border-purple-200" },
  { id: "question", label: "Question", icon: HelpCircle, tone: "bg-blue-100 text-blue-800 border-blue-200" },
  { id: "billing", label: "Billing", icon: CreditCard, tone: "bg-amber-100 text-amber-800 border-amber-200" },
  { id: "other", label: "Other", icon: MoreHorizontal, tone: "bg-slate-100 text-slate-800 border-slate-200" },
];
const TYPE_BY_ID = Object.fromEntries(THREAD_TYPES.map((t) => [t.id, t]));

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
}

export default function AdminSupport() {
  const { user } = useAuth();
  const { threadId } = useParams();
  const nav = useNavigate();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingThread, setLoadingThread] = useState(false);
  const [filter, setFilter] = useState("open"); // open | all | closed

  useEffect(() => {
    if (!user) return;
    if (user.role !== "platform_admin") nav("/dashboard");
  }, [user, nav]);

  const loadList = useCallback(async () => {
    try {
      const { data } = await api.get("/support/threads");
      setThreads(data.threads || []);
    } catch {} finally { setLoadingList(false); }
  }, []);

  const loadThread = useCallback(async (id) => {
    setLoadingThread(true);
    try {
      const { data } = await api.get(`/support/threads/${id}`);
      setActive(data.thread);
      setMessages(data.messages || []);
      // Reflect "unread cleared" in the side list immediately
      setThreads((prev) => prev.map((t) => t.id === id ? { ...t, unread_for_admin: 0 } : t));
    } catch {
      toast.error("Couldn't load thread");
    } finally { setLoadingThread(false); }
  }, []);

  useEffect(() => { loadList(); const i = setInterval(loadList, 25_000); return () => clearInterval(i); }, [loadList]);
  useEffect(() => { if (threadId) loadThread(threadId); }, [threadId, loadThread]);

  const filtered = threads.filter((t) => filter === "all" ? true : t.status === filter);

  return (
    <div className="min-h-screen bg-[#FBFAF6]" data-testid="admin-support-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-slate-500 hover:text-slate-700"><ArrowLeft className="w-5 h-5" /></Link>
            <div className="w-10 h-10 rounded-xl bg-[#C75D33]/10 text-[#C75D33] flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-slate-900">Support inbox</h1>
              <p className="text-xs text-slate-500">Threads from every company · oldest open first</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 grid lg:grid-cols-12 gap-6">
        {/* Threads list */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="px-3 py-3 border-b border-slate-200 flex items-center gap-1.5">
              {(["open", "all", "closed"]).map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  className={`text-xs font-bold px-2.5 py-1.5 rounded-lg ${filter === f ? "bg-[#0F172A] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid={`admin-support-filter-${f}`}
                >
                  {f === "open" && "Open"}
                  {f === "all" && "All"}
                  {f === "closed" && "Closed"}
                </button>
              ))}
              <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-slate-400">{filtered.length}</span>
            </div>
            {loadingList ? (
              <div className="px-5 py-10 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="px-5 py-12 text-center text-sm text-slate-500">No threads.</div>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto">
                {filtered.map((t) => {
                  const meta = TYPE_BY_ID[t.type] || TYPE_BY_ID.other;
                  const unread = (t.unread_for_admin || 0) > 0;
                  return (
                    <li key={t.id}>
                      <Link
                        to={`/admin/support/${t.id}`}
                        className={`block px-4 py-3 hover:bg-slate-50 transition-colors ${active?.id === t.id ? "bg-slate-50" : ""}`}
                        data-testid={`admin-support-thread-${t.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.tone}`}>
                            <meta.icon className="w-3 h-3" /> {meta.label}
                          </span>
                          {t.status === "closed" && (
                            <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">closed</span>
                          )}
                          {unread && <span className="ml-auto bg-[#C75D33] text-white text-[10px] font-bold rounded-full min-w-[18px] h-4 flex items-center justify-center px-1">{t.unread_for_admin}</span>}
                        </div>
                        <div className={`text-sm ${unread ? "font-extrabold text-slate-900" : "font-semibold text-slate-800"}`}>{t.subject}</div>
                        <div className="text-xs text-slate-500 mt-0.5 truncate">{t.company_name}</div>
                        <div className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">{fmtTime(t.last_message_at)}</div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>

        {/* Detail */}
        <section className="lg:col-span-8 xl:col-span-9">
          {!threadId ? (
            <div className="bg-white border border-slate-200 rounded-2xl px-6 py-16 text-center text-sm text-slate-500">
              <MessageCircle className="w-7 h-7 mx-auto mb-2 text-slate-300" />
              Pick a thread from the left to view the conversation.
            </div>
          ) : (
            <ThreadDetail
              loading={loadingThread}
              thread={active}
              messages={messages}
              onReplySent={() => { loadThread(threadId); loadList(); }}
              onStatusChange={() => { loadThread(threadId); loadList(); }}
            />
          )}
        </section>
      </main>
    </div>
  );
}

function ThreadDetail({ loading, thread, messages, onReplySent, onStatusChange }) {
  const [reply, setReply] = useState("");
  const [imageUrl, setImageUrl] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  if (loading || !thread) {
    return <div className="bg-white border border-slate-200 rounded-2xl px-6 py-16 text-center text-sm text-slate-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" /> Loading…</div>;
  }

  const meta = TYPE_BY_ID[thread.type] || TYPE_BY_ID.other;

  const send = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await api.post(`/support/threads/${thread.id}/messages`, { body: reply.trim(), image_url: imageUrl });
      setReply(""); setImageUrl(null);
      onReplySent();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send");
    } finally { setSending(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/support/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.image_url);
      toast.success("Image attached");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const toggleStatus = async () => {
    const action = thread.status === "open" ? "close" : "reopen";
    try {
      await api.post(`/support/threads/${thread.id}/${action}`);
      toast.success(thread.status === "open" ? "Thread closed" : "Thread reopened");
      onStatusChange();
    } catch (e) { toast.error("Couldn't update"); }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start sm:items-center gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1.5">
            <span className={`inline-flex items-center gap-1 text-[10px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${meta.tone}`}>
              <meta.icon className="w-3 h-3" /> {meta.label}
            </span>
            {thread.status === "closed" && (
              <span className="text-[10px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">closed</span>
            )}
          </div>
          <div className="font-display font-bold text-base text-slate-900 truncate">{thread.subject}</div>
          <div className="text-xs text-slate-500 mt-0.5">From <strong className="text-slate-800">{thread.company_name}</strong> · {thread.owner_email}</div>
        </div>
        <Button variant="outline" size="sm" onClick={toggleStatus} className="rounded-lg" data-testid="admin-support-toggle-status">
          {thread.status === "open" ? <><Lock className="w-3.5 h-3.5 mr-1" /> Close thread</> : <><Unlock className="w-3.5 h-3.5 mr-1" /> Reopen thread</>}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 bg-slate-50 space-y-3 max-h-[55vh]" data-testid="admin-support-messages">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender_role === "admin" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${m.sender_role === "admin" ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-900"}`}>
              <div className={`text-[10px] font-extrabold uppercase tracking-widest mb-1 ${m.sender_role === "admin" ? "text-yellow-300" : "text-[#C75D33]"}`}>
                {m.sender_role === "admin" ? "You" : (thread.company_name || "Company")}
              </div>
              <div className="whitespace-pre-wrap text-sm leading-relaxed">{m.body}</div>
              {m.image_url && (
                <a href={m.image_url} target="_blank" rel="noreferrer" className="block mt-2">
                  <img src={m.image_url} alt="attachment" className="rounded-lg max-h-56 border border-black/5" />
                </a>
              )}
              <div className={`text-[10px] mt-1.5 ${m.sender_role === "admin" ? "text-white/55" : "text-slate-400"}`}>{fmtTime(m.created_at)}</div>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="border-t border-slate-200 bg-white p-4">
        <Textarea
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          rows={3}
          placeholder="Reply…"
          className="rounded-xl resize-none"
          maxLength={8000}
          data-testid="admin-support-reply-input"
        />
        <div className="flex items-center justify-between gap-2 mt-2">
          {imageUrl ? (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <img src={imageUrl} alt="attached" className="h-8 w-8 rounded object-cover border border-slate-200" />
              <span className="font-semibold">Image attached</span>
              <button onClick={() => setImageUrl(null)} className="text-slate-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-900" data-testid="admin-support-attach-btn">
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
              {uploading ? "Uploading…" : "Attach image"}
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
          <Button onClick={send} disabled={sending || uploading || !reply.trim()} className="bg-[#C75D33] hover:bg-[#A84C28] rounded-xl h-10 px-5 font-bold" data-testid="admin-support-send-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" /> Send reply</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
